import { toast } from 'react-toastify';
import SocketV2Service from '../socket/socket-v2.service';
import RabbitStompService from '../rabbit-stomp/rabbit-stomp.service';

export type ActionType = 'record' | 'update' | 'revoke' | 'reset';

export interface ActionPayload {
  promotionId?: string;
  /** Corpo da action, no formato que os handlers do mike-goldberg-v2 esperam. */
  data: unknown;
}

interface ActionEnvelope extends ActionPayload {
  timestamp: string;
}

/** Corpo do ack devolvido pelo gateway do mike-goldberg-v2. */
interface ServerAck {
  success: boolean;
  action?: unknown;
  error?: string;
  code?: 'invalid_payload' | 'not_found' | 'internal_error';
  timestamp: string;
}

/** Resultado de um envio. */
export interface SendActionResult {
  /** `false` quando a ação foi para o outbox e será reenviada. */
  delivered: boolean;
  /**
   * Documento persistido, quando o transporte devolve ack — só o websocket.
   * É por aqui que o cliente descobre o `_id` sem depender do eco `recorded`.
   */
  action?: unknown;
}

interface OutboxEntry {
  type: ActionType;
  envelope: ActionEnvelope;
  /** Momento do primeiro enfileiramento, para descartar entradas velhas. */
  queuedAt: number;
}

const OUTBOX_KEY = '@Scout:actionsOutbox';
const TOKEN_KEY = '@Scout:accessToken';
const ACK_TIMEOUT_MS = 8000;
const DRAIN_RETRY_MS = 5000;
const MAX_OUTBOX_SIZE = 500;
/** Ações mais velhas que isso não valem mais nada: a luta já acabou. */
const MAX_OUTBOX_AGE_MS = 6 * 60 * 60 * 1000;

const transport: 'ws' | 'rabbitmq' =
  process.env.REACT_APP_ACTIONS_TRANSPORT === 'ws' ? 'ws' : 'rabbitmq';

let draining = false;
let drainScheduled = false;

// ───────────────────────────── transporte ─────────────────────────────

/**
 * Tenta entregar uma vez. `delivered: false` quando vale a pena reenviar depois,
 * e `true` quando a entrega foi concluída ou quando reenviar não adianta
 * (payload inválido, ação inexistente). `action` traz o documento persistido.
 */
async function deliverViaSocket(
  type: ActionType,
  envelope: ActionEnvelope,
): Promise<SendActionResult> {
  if (!SocketV2Service.hasInstance()) return { delivered: false };

  const socket = SocketV2Service.getInstance();
  if (!socket.isConnected()) return { delivered: false };

  try {
    // `ack.success` é o nível de transporte (a resposta chegou antes do
    // timeout); `ack.data` é o ScoutActionAck devolvido pelo gateway.
    const ack = await socket.emitWithAck<ServerAck>(
      type,
      envelope,
      ACK_TIMEOUT_MS,
    );
    if (!ack.success) return { delivered: false };

    const serverAck = ack.data;
    // O documento vem no ack: é assim que o cliente sabe o `_id` de uma action
    // recém-gravada sem esperar o eco `recorded` chegar pela sala.
    if (serverAck?.success) {
      return { delivered: true, action: serverAck.action };
    }

    // O servidor respondeu recusando: reenviar não muda o resultado.
    if (
      serverAck?.code === 'invalid_payload' ||
      serverAck?.code === 'not_found'
    ) {
      toast.warn(`Ação não aplicada: ${serverAck.error ?? 'não encontrada'}`);
      return { delivered: true };
    }

    return { delivered: false };
  } catch {
    // Socket desconectou entre o check e o emit.
    return { delivered: false };
  }
}

/** Transporte legado: entrega sem ack, então nunca devolve o documento. */
async function deliverViaRabbit(
  type: ActionType,
  envelope: ActionEnvelope,
): Promise<SendActionResult> {
  const rabbit = RabbitStompService.getInstance();
  if (!rabbit.isConnected()) return { delivered: false };

  try {
    await rabbit.sendToQueue(
      JSON.stringify({
        type,
        token: localStorage.getItem(TOKEN_KEY) ?? '',
        promotionId: envelope.promotionId,
        data: envelope.data,
        timestamp: envelope.timestamp,
      }),
    );
    return { delivered: true };
  } catch {
    return { delivered: false };
  }
}

const deliver = (
  type: ActionType,
  envelope: ActionEnvelope,
): Promise<SendActionResult> =>
  transport === 'rabbitmq'
    ? deliverViaRabbit(type, envelope)
    : deliverViaSocket(type, envelope);

// ─────────────────────────────── outbox ───────────────────────────────

function readOutbox(): OutboxEntry[] {
  try {
    const raw = localStorage.getItem(OUTBOX_KEY);
    const entries: OutboxEntry[] = raw ? JSON.parse(raw) : [];
    const cutoff = Date.now() - MAX_OUTBOX_AGE_MS;
    return entries.filter((entry) => entry.queuedAt > cutoff);
  } catch {
    return [];
  }
}

function writeOutbox(entries: OutboxEntry[]): void {
  try {
    localStorage.setItem(
      OUTBOX_KEY,
      JSON.stringify(entries.slice(-MAX_OUTBOX_SIZE)),
    );
  } catch (error) {
    toast.error('Falha ao salvar a ação pendente localmente.');
  }
}

function scheduleDrain(): void {
  if (drainScheduled) return;
  drainScheduled = true;
  setTimeout(() => {
    drainScheduled = false;
    // eslint-disable-next-line no-use-before-define
    drainActionOutbox();
  }, DRAIN_RETRY_MS);
}

function enqueue(entry: OutboxEntry): void {
  writeOutbox([...readOutbox(), entry]);
  toast.warn('Sem conexão: ação salva e será reenviada.', {
    toastId: 'action-outbox',
  });
  scheduleDrain();
}

/**
 * Reenvia o outbox em ordem. Para na primeira falha para não inverter a ordem
 * das ações — o resto fica na fila para a próxima tentativa.
 */
export async function drainActionOutbox(): Promise<void> {
  if (draining) return;
  draining = true;

  try {
    let entries = readOutbox();
    if (entries.length === 0) return;

    while (entries.length > 0) {
      const [next, ...rest] = entries;
      // Sequencial de propósito: a ordem das ações da luta precisa ser mantida.
      // eslint-disable-next-line no-await-in-loop
      const { delivered } = await deliver(next.type, next.envelope);
      if (!delivered) break;

      entries = rest;
      writeOutbox(entries);
    }

    if (entries.length === 0) {
      toast.dismiss('action-outbox');
      toast.success('Ações pendentes reenviadas.');
    } else {
      scheduleDrain();
    }
  } finally {
    draining = false;
  }
}

// ──────────────────────────────── API ────────────────────────────────

/**
 * Ponto único de envio de actions do scout para o mike-goldberg-v2.
 *
 * Substitui os `if (isConnectedRabbitMQ) publish(...)` espalhados pelos call
 * sites, que descartavam a ação em silêncio quando o transporte estava fora.
 * Aqui, o que não sai vai para um outbox em localStorage e é reenviado na
 * reconexão.
 *
 * Nunca rejeita: falha vira outbox + aviso, para que um problema de rede não
 * interrompa o registro da luta.
 */
export async function sendAction(
  type: ActionType,
  payload: ActionPayload,
): Promise<SendActionResult> {
  const envelope: ActionEnvelope = {
    ...payload,
    timestamp: new Date().toISOString(),
  };

  const result = await deliver(type, envelope);
  if (!result.delivered) {
    enqueue({ type, envelope, queuedAt: Date.now() });
  }

  return result;
}

export const pendingActionsCount = (): number => readOutbox().length;

export const actionsTransport = transport;
